/**
 * Upsell Swatch & Quick Add System
 * ---------------------------------
 * This script handles synchronization between the main product swatches
 * and upsell product cards, including swatch selection, variant availability,
 * and quick add-to-cart functionality.
 *
 * Core Features:
 * 1. syncUpsellSwatch(starterColorRaw):
 *    - Synchronizes upsell swatches with the selected main product color,
 *      a fallback color, or defaults to the first swatch.
 *    - Updates the visible variant label inside upsell cards.
 *    - Dispatches synthetic click events on swatches to trigger native behaviors.
 *
 * 2. Initial Variant Text Sync:
 *    - On DOM load, updates "outside color name" elements with the currently 
 *      active variant title from each product card.
 *
 * 3. Swatch Click Handling:
 *    - Globally listens for swatch clicks.
 *    - Updates the active swatch state inside upsell cards.
 *    - Synchronizes the outside displayed color text.
 *    - Controls visibility of "Quick Add" or "Notify Me" buttons based on 
 *      variant availability.
 *
 * 4. Starter Kit Flow (Swiper Integration):
 *    - Monitors visible swipers to detect active colors.
 *    - Uses MutationObserver + polling to react to dynamic DOM changes.
 *    - Ensures upsell cards stay in sync with swipers when colors change.
 *
 * 5. Quick Add to Cart:
 *    - Handles click events on "Quick Add" buttons in upsell cards.
 *    - Finds the active or default variant ID.
 *    - Calls Shopify’s AJAX API (`/cart/add.js`) to add the variant to the cart.
 *    - Dispatches a custom event `ajaxProduct:added` with cart item details.
 *    - Updates button states (loading, added, reset).
 *    - Temporarily disables interactions to prevent double-clicks.
 *
 * 6. Error Handling:
 *    - Logs missing variant IDs.
 *    - Catches and logs AJAX add-to-cart errors.
 *
 * Dependencies:
 * - Shopify storefront with product swatches.
 * - Proper HTML structure for `.upsells-card`, `.swatch`, `.quick-buy`, 
 *   `.variant-title`, `.outside-color-name`, etc.
 *
 * Usage:
 * - Included on pages with upsell products (e.g., product detail or starter kit flow).
 * - Auto-runs on DOMContentLoaded and listens for user interactions dynamically.
 */
function syncUpsellSwatch(starterColorRaw = null) {
    // Priority: if starterColor is passed, use it, otherwise check main product selection
    const starterColor = starterColorRaw?.toLowerCase().trim();
    const selectedColor = starterColor || document.querySelector('input[data-input-purpose="swatch"]:checked')?.value?.toLowerCase().trim();

    document.querySelectorAll(".upsells-card product-card").forEach((productCard) => {
        const fallbackColor = productCard?.getAttribute("fallback-color")?.toLowerCase().trim();
        const upsellSwatches = productCard.querySelectorAll('.swatch');
        let matchFound = false;

        // Reset all swatches
        upsellSwatches.forEach((swatch) => swatch.classList.remove('active'));

        // 1. Try matching selected/starter color
        if (selectedColor) {
            upsellSwatches.forEach((swatch) => {
                const upsellColor = swatch?.getAttribute('data-variant-name')?.toLowerCase().trim() || "";
                if (!matchFound && selectedColor === upsellColor) {
                    swatch.classList.add('active');
                    matchFound = true;
                    swatch.dispatchEvent(new MouseEvent("click", {
                        bubbles: true
                    }));
                    const labelEl = productCard.querySelector('.font-estrid-cru');
                    if (labelEl) labelEl.textContent = swatch.getAttribute('data-variant-name');
                }
            });
        }

        // 2. If no match, try fallback color
        if (!matchFound && fallbackColor) {
            upsellSwatches.forEach((swatch) => {
                const upsellColor = swatch?.getAttribute('data-variant-name')?.toLowerCase().trim() || "";
                if (!matchFound && fallbackColor === upsellColor) {
                    swatch.classList.add('active');
                    matchFound = true;
                    swatch.dispatchEvent(new MouseEvent("click", {
                        bubbles: true
                    }));
                    const labelEl = productCard.querySelector('.font-estrid-cru');
                    if (labelEl) labelEl.textContent = swatch.getAttribute('data-variant-name');
                }
            });
        }

        // 3. Default fallback → first swatch
        if (!matchFound && upsellSwatches.length > 0) {
            const defaultSwatch = upsellSwatches[0];
            defaultSwatch.classList.add('active');
            defaultSwatch.dispatchEvent(new MouseEvent("click", {
                bubbles: true
            }));
            const labelEl = productCard.querySelector('.font-estrid-cru');
            if (labelEl) labelEl.textContent = defaultSwatch.getAttribute('data-variant-name');
        }
    });
}

// Upsell Bottom text with color and product-names
function upsellUpdateText(card, selectedColor = null){
    const colorTextEl = card.querySelector('.color-text');
    const productTextEl = card.querySelector('.product-text');
    const dynamicTextEl = card.querySelector('.upsell-dynamic-text');

    if (!dynamicTextEl) return;
    const template = card.querySelector('p[data-template]')?.dataset.template || dynamicTextEl.dataset.template || "[product]";
    const upsellColorsText = selectedColor || colorTextEl?.textContent.trim() || '';
    const upsellProductText = productTextEl?.textContent.trim() || '';
    const combined = upsellColorsText ? `${upsellColorsText} - ${upsellProductText}` : upsellProductText;
    dynamicTextEl.textContent = template.replace('[product]', combined);
}

document.addEventListener("DOMContentLoaded", function() {

    // Initial load: grab all product-cards and sync variant title outside
    document.querySelectorAll("product-card").forEach((card) => {
        const productId = card.getAttribute("product-id");
        const variantTitleEl = card.querySelector(".variant-title");

        if (variantTitleEl) {
            const color = variantTitleEl.textContent.trim();
            const outsideText = document.querySelector(`.outside-color-name[data-product-id="${productId}"] .color-text`);
            if (outsideText && color) {
                outsideText.textContent = color;
            }
        }
    });

    // On swatch click: listen globally
    document.body.addEventListener("click", function(e) {
        const swatch = e.target.closest(".swatch");
        if (!swatch) return;

        if (document.body.classList.contains("layout--starter-kit")) {
            const newColor = swatch.getAttribute("data-variant-name");
        }
        const productCard = swatch.closest("product-card");
        if (!productCard) return;
        const productId = productCard.getAttribute("product-id");

        // Wait for variant title to update in DOM
        setTimeout(() => {
            const variantTitleEl = productCard.querySelector(".font-estrid-cru");

            if (variantTitleEl) {
                const updatedColor = variantTitleEl.textContent.trim();
                const outsideText = document.querySelector(`.outside-color-name[data-product-id="${productId}"] .color-text`);
                if (outsideText && updatedColor) {
                    outsideText.textContent = updatedColor;
                }
            }
        }, 100); // slight delay to allow DOM to update

        // Update only clicked card text
        const card = swatch.closest('.upsells-card');
        if (card) {
            const variantName = swatch.dataset.variantName || '';
            upsellUpdateText(card, variantName);
        }
    });

    // Run on load
    syncUpsellSwatch();

    // Change and update upsell bottom text with upsellUpdateText function
    document.querySelectorAll('.upsells-card').forEach((card) => {
        const activeSwatch = card.querySelector('.swatch.active');
        const variantName = activeSwatch ? activeSwatch.dataset.variantName : card.querySelector('.color-text')?.textContent.trim() || '';
        upsellUpdateText(card, variantName);
    });

    // Run on color change
    document.querySelectorAll('input[data-input-purpose="swatch"]').forEach((input) => {
        input.addEventListener('change', () => {
            syncUpsellSwatch();
        });
    });

    const flowEl = document.getElementById("get-started-flow");
    if (flowEl) {
        (() => {
            const swipers = Array.from(document.querySelectorAll('.swiper'));
            swipers.forEach((el, i) => {
                if (!el.dataset.swiperId) el.dataset.swiperId = String(i);
            });

            const lastColorMap = new WeakMap();

            const getActiveColor = (swiper) => {
                const sw = swiper.querySelector('.swatch.active');
                return sw?.getAttribute('data-variant-name') || null;
            };

            const isVisible = (el) => {
                return !!(el && el.offsetParent !== null && getComputedStyle(el).display !== "none" && getComputedStyle(el).visibility !== "hidden");
            };

            const maybeSync = (swiper) => {
                if (!swiper || !isVisible(swiper)) return;

                const current = getActiveColor(swiper);
                if (!current) return;
                const prev = lastColorMap.get(swiper);
                if (current !== prev) {
                    lastColorMap.set(swiper, current);
                    syncUpsellSwatch(current, swiper.dataset.swiperId);
                }
            };

            //  Polling to detect final visible slider
            let pollCount = 0;
            const pollForSlider = setInterval(() => {
                pollCount++;
                const visibleSwipers = swipers.filter((swiper) => isVisible(swiper));
                if (visibleSwipers.length > 0) {
                    const activeSwiper = visibleSwipers[visibleSwipers.length - 1]; // last visible
                    const initial = getActiveColor(activeSwiper);
                    if (initial) {
                        lastColorMap.set(activeSwiper, initial);
                        syncUpsellSwatch(initial, activeSwiper.dataset.swiperId);
                        clearInterval(pollForSlider); // stop polling once done
                    }
                }
                if (pollCount > 20) {
                    clearInterval(pollForSlider);
                }
            }, 300);

            // MutationObserver for dynamic changes
            const observer = new MutationObserver((mutations) => {
                for (const m of mutations) {
                    if (m.type === 'attributes' && m.attributeName === 'class') {
                        const el = m.target;
                        if (el.classList?.contains('swatch')) {
                            const swiper = el.closest('.swiper');
                            if (swiper) maybeSync(swiper);
                        }
                    } else if (m.type === 'childList') {
                        m.addedNodes.forEach((node) => {
                            if (!(node instanceof Element)) return;
                            const swatch =
                                node.matches?.('.swatch') ? node : node.querySelector?.('.swatch');
                            if (swatch) {
                                const swiper = swatch.closest('.swiper');
                                if (swiper) maybeSync(swiper);
                            }
                        });
                    }
                }
            });

            observer.observe(document.body, {
                attributes: true,
                attributeFilter: ['class'],
                childList: true,
                subtree: true,
            });

            // Click fallback
            document.addEventListener('click', (e) => {
                const swatch = e.target.closest?.('.swatch');
                if (!swatch) return;
                const swiper = swatch.closest('.swiper');
                if (!swiper) return;
                queueMicrotask(() => maybeSync(swiper));
            });
        })();
    }
});

document.querySelectorAll('.upsells-card .swatch').forEach((upsellSwatch) => {
    upsellSwatch.addEventListener("click", function(e) {
        
        // closest upsell card
        const card = this.closest(".upsells-card");

        // Set clicked swatch active
        card.querySelectorAll(".swatch").forEach(swatch => swatch.classList.remove("active"));
        this.classList.add("active");

        // get variant
        const variantId = this.dataset.variantId;
        if (!variantId) return;

        // quickbuy and notifyme button selectors
        const quickBuyBtns = card.querySelectorAll(".upsell-card-main-content .card-actions .js.quick-buy");
        const notifyBtns = card.querySelectorAll(".upsell-card-main-content .card-actions a.btn");

        // hide buttons first
        quickBuyBtns.forEach(btn => btn.style.display = "none");
        notifyBtns.forEach(btn => btn.style.display = "none");

        // check variant availability
        const isVariantAvailable = this.dataset.variantAvailable;
        
        // show appropriate buttons on the basis of availability
        if (isVariantAvailable === "true") {
            // show all Quick Add buttons
            quickBuyBtns.forEach(btn => btn.style.display = "inline-flex");
            const variantColors = this.dataset.variantName || '';
            upsellUpdateText(card, variantColors);
        } else {
            // show all Notify Me buttons
            notifyBtns.forEach(btn => btn.style.display = "inline-flex");
        }
    });
})

//--------------------------------------------------
// Quick Add Click → Add to Cart
//--------------------------------------------------
document.addEventListener("click", function(e) {
    const btn = e.target.closest(".upsell-card-main-content .card-actions .js.quick-buy");
    if (!btn) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;

    e.preventDefault();
    const plusIcon = btn.querySelector('.quick-buy__icon')
    const textQuickAdd = btn.querySelector('.quick-buy__text');
    const textAdded = btn.querySelector('.quick-buy__Added');
    const overlay = btn.querySelector('.loading-overlay');

    if (textQuickAdd) {
        textQuickAdd.style.display = 'none';
        plusIcon.style.display = 'none';  
    } 
    if (overlay) overlay.classList.remove('hidden');

    const card = btn.closest(".upsells-card");
    // Variant ID handling
    let variantId = null;
    // 1. If swatches are available then show
    const activeSwatch = card?.querySelector(".swatch.active");
    if (activeSwatch) {
        variantId = activeSwatch.dataset.variantId;
    }
    // 2. If swatches are not available then it take default variant
    if (!variantId) {
        const defaultVariantEl = card?.querySelector("[data-default-variant-id]");
        if (defaultVariantEl) {
            variantId = defaultVariantEl.dataset.defaultVariantId;
        }
    }
    if (!variantId) {
        console.error("No variant ID found for upsell card", card);
        return;
    }

    // Disable interactions while adding
    card.classList.add("is-loading");
    card.querySelectorAll(".swatch, .quick-buy").forEach(el => {
        el.style.pointerEvents = "none";
        el.style.opacity = "0.5"; 
    });

    // Shopify AJAX Add to Cart
    fetch("/cart/add.js", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json"
        },
        body: JSON.stringify({
            id: variantId,
            quantity: 1
        }),
    })
    .then(res => res.json())
    .then(cartItem => {
        const event = new CustomEvent('ajaxProduct:added', {
            detail: {
            cartItem
            }
        });
        document.dispatchEvent(event)

        // Update Quick Add button states
        if (overlay) overlay.classList.add('hidden');
        if (textAdded) textAdded.style.display = 'block';

        setTimeout(() => {
            if (textAdded) textAdded.style.display = 'none';
            if (textQuickAdd) textQuickAdd.style.display = 'block';
            if (plusIcon) plusIcon.style.display = 'block';
        }, 1000);

        card.classList.remove("is-loading");
        card.querySelectorAll(".swatch, .quick-buy").forEach(el => {
            el.style.pointerEvents = "";
            el.style.opacity = "";
        });            
    })
    .catch(err => console.error("Add to cart error:", err));    
});
